import React, { useEffect, useRef, useState } from 'react';
import { DeviceType } from '../../../enum/EnumRepository';
import { useDeviceStore } from '../../../store/deviceStore';
import styled from 'styled-components';
import { paperTextureSmall } from '../../paperTexture';

const HINT_ABOVE_ROOM = 40;

const getScrollParent = (el: HTMLElement): HTMLElement | null => {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
};

interface HintWrapperProps {
  children: React.ReactNode;
  content: string;
  content2?: string;
  toLeft?: boolean;
}

const HintWrapper: React.FC<HintWrapperProps> = ({ children, content, content2, toLeft }) => {
  const { deviceType } = useDeviceStore();
  const [isHover, setIsHover] = useState(false);
  const [openBelow, setOpenBelow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHover) return;
    const el = wrapperRef.current;
    if (!el) return;
    const root = getScrollParent(el);
    if (!root) return;

    const update = () => {
      const roomAbove = el.getBoundingClientRect().top - root.getBoundingClientRect().top;
      setOpenBelow(roomAbove < HINT_ABOVE_ROOM);
    };

    update();
    root.addEventListener('scroll', update, { passive: true });
    return () => root.removeEventListener('scroll', update);
  }, [isHover]);

  if (deviceType === DeviceType.MOBILE) {
    return <>{children}</>;
  }

  return (
    <HintHoverWrapper
      ref={wrapperRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}>
      <HintCard $isHover={isHover} $toLeft={toLeft} $below={openBelow}>
        {content}
        {content2 && <br />}
        {content2 && content2}
      </HintCard>
      {children}
    </HintHoverWrapper>
  );
}

const HintHoverWrapper = styled.div`
  position: relative;
`;

const HintCard = styled.div<{ $isHover: boolean; $toLeft?: boolean; $below?: boolean }>`
  position: absolute;
  ${({ $below }) => $below
    ? 'top: calc(100% + 6px);'
    : 'bottom: clamp(35px, 4vh, 40px);'}
  ${({ $toLeft }) => $toLeft && 'right: 0;'}
  width: fit-content;
  min-width: 50px;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 0.5vw;
  padding: 0.6vw 0.8vw;
  
  ${({ theme }) => theme.typography.Body_04};
  white-space: nowrap;
  ${paperTextureSmall}

  /* Hover animations */
  opacity: ${({ $isHover }) => ($isHover ? 1 : 0)};
  transform: translateY(${({ $isHover, $below }) => ($isHover ? '0' : $below ? '-20px' : '20px')});
  transition: opacity 0.3s ease, transform 0.3s ease;
  pointer-events: ${({ $isHover }) => ($isHover ? 'auto' : 'none')};
`;

export default HintWrapper;